import {useJwtUser} from "../JWTAuthProvider";
import {ClubSubscriptionTier, PlayerSubscriptionTier, UserRole} from "../constants";

/**
 * Fallback for when there is no user yet — logged out, or the profile has not loaded.
 *
 * Deliberately DENY-ALL, with no invented numbers. It used to claim max_entries_per_league: 6
 * and max_concurrent_leagues: 1, neither of which the server has ever returned (both are
 * unlimited), so any UI reading them showed limits that did not exist. An absent answer has to
 * look absent rather than like a different answer — the server is the only authority here.
 */
function getDefaultAllowedTo() {
    return {
        create_tournaments:     0,
        create_leagues:         0,
        organizer_tier:         ClubSubscriptionTier.Free,
        organizer_tier_label:   'Free',
        max_entries_per_league: 0,
        max_concurrent_leagues: 0,
    } as unknown as Record<string, boolean | number | string>;
}

export function useSubscription() {
    const user = useJwtUser();

    const userRole = user?.role_override || user?.role;
    const isOrganizer = userRole === UserRole.Organizer;

    const hasClubSubscription = isOrganizer &&
        user?.club?.subscription_tier !== undefined &&
        // Basic (150) is the $19 "Starter" plan. This read >= Starter (200, which sells as
        // "Plus"), so a paying $19 club counted as unsubscribed everywhere this is used.
        user.club.subscription_tier >= ClubSubscriptionTier.Basic;

    const isSubscribed = user?.is_subscribed || hasClubSubscription;
    // Genuinely-paid access, distinct from isSubscribed (which is forced open for all organizers).
    // Gates the per-event one-off pricing: a paid plan includes one-off events; free organizers pay.
    const hasPaidPlan = (user as any)?.has_paid_plan || hasClubSubscription;
    const isOnTrial = isSubscribed && (user?.trial_seconds_left || 0) > 0 && !hasClubSubscription;
    const hasPaidSubscription = isSubscribed && !isOnTrial;
    const trialSecondsLeft = user?.trial_seconds_left || 0;
    const trialDaysLeft = trialSecondsLeft > 0 ? Math.ceil(trialSecondsLeft / 86400) : 0;
    const hasActiveTrial = isOnTrial;
    const tier = user?.subscription_tier ?? PlayerSubscriptionTier.Free;
    const allowedTo = user?.allowed_to ?? getDefaultAllowedTo();
    const organizerTier = Number(allowedTo.organizer_tier ?? ClubSubscriptionTier.Free);

    const hasFeature = (feature: string): boolean => {
        const value = (allowedTo as unknown as Record<string, boolean | number>)[feature];
        return value === true || (typeof value === 'number' && value > 0);
    };

    const hasOrganizerFeature = (feature: string): boolean => {
        const value = (allowedTo as unknown as Record<string, boolean | number>)[feature];
        return value === true;
    };

    const canCreate = (_type: 'league' | 'tournament', currentCount: number): boolean => {
        const maxLeagues = Number(allowedTo.max_concurrent_leagues ?? 0);
        if (maxLeagues > 10000) {
            return true; // unlimited
        }
        return currentCount < maxLeagues;
    };

    return {
        isSubscribed,
        hasPaidPlan,
        isOnTrial,
        hasActiveTrial,
        trialSecondsLeft,
        trialDaysLeft,
        hasPaidSubscription,
        hasClubSubscription,
        tier,
        clubTier: user?.club?.subscription_tier,
        isPro: tier === PlayerSubscriptionTier.Pro || hasClubSubscription,
        isFree: tier === PlayerSubscriptionTier.Free && !hasClubSubscription,
        allowedTo,
        hasFeature,

        // Organizer tier
        organizerTier,
        organizerTierLabel: String(allowedTo.organizer_tier_label ?? 'Free'),
        hasOrganizerFeature,
        maxEntriesPerLeague: Number(allowedTo.max_entries_per_league ?? 0),
        maxConcurrentLeagues: Number(allowedTo.max_concurrent_leagues ?? 0),

        canCreate,
    };
}
